// src/utils/mlDataPreprocessor.js

/**
 * Клас для підготовки даних для Machine Learning
 * Конвертує сирі дані з бази в структуровані features для ML
 */
export class MLDataPreprocessor {
  
  /**
   * Основна функція нормалізації запису
   * Перетворює сирий запис звіту в структуровані дані для ML
   */
  static normalizeRecord(record) {
    if (!record || !record.fecha) {
      console.warn('Некоректний запис для нормалізації:', record);
      return null;
    }

    const date = new Date(record.fecha);
    
    return {
      // === МЕТАДАНІ ===
      id: record.id,
      date: record.fecha,
      timestamp: date.getTime(),
      
      // === ЧАСОВІ ОЗНАКИ (Time Features) ===
      timeFeatures: {
        dayOfWeek: date.getDay(),           // 0-6 (неділя-субота)
        dayOfMonth: date.getDate(),         // 1-31
        month: date.getMonth(),             // 0-11
        year: date.getFullYear(),
        isWeekend: [0, 6].includes(date.getDay()) ? 1 : 0, // 1 якщо вихідний
        season: this.getSeason(date.getMonth()) // 0-3 (зима, весна, літо, осінь)
      },
      
      // === ОЗНАКИ ЗМІНИ (Shift Features) ===
      shiftFeatures: {
        shiftType: record.turno === 'mañana' ? 0 : 1, // 0-ранкова, 1-вечірня
        operatorsCount: record.operators?.length || 0,
        shiftDuration: this.calculateShiftDuration(record), // тривалість у годинах
        hasMultipleOperators: (record.operators?.length || 0) > 1 ? 1 : 0
      },
      
      // === ВИРОБНИЦТВО (Production Features) ===
      productionFeatures: {
        totalProduction: this.calculateTotalProduction(record.tunel),
        activeProgramsCount: this.countActivePrograms(record.tunel),
        productionByProgram: this.normalizePrograms(record.tunel),
        rejectionRate: this.calculateRejectionRate(record),
        efficiency: this.calculateEfficiency(record)
      },
      
      // === РЕСУРСИ (Resource Features) ===
      resourceFeatures: {
        waterConsumption: this.parseConsumption(record.cont?.aguaTunel),
        propaneConsumption: this.parseConsumption(record.cont?.propCaldera),
        electricityConsumption: this.estimateElectricity(record),
        totalCycles: this.calculateTotalCycles(record.lav_ciclos)
      },
      
      // === ЦІЛЬОВІ ЗМІННІ (Target Variables) ===
      targets: {
        nextDayProduction: 0, // Буде заповнено під час тренування
        productionTrend: 0,   // Тренд порівняно з попереднім днем
        resourceEfficiency: 0 // Ефективність використання ресурсів
      },
      
      // === ЯКІСТЬ ДАНИХ ===
      dataQuality: {
        isComplete: this.isRecordComplete(record),
        hasProduction: this.calculateTotalProduction(record.tunel) > 0,
        hasResources: this.hasResourceData(record.cont),
        confidence: this.calculateDataConfidence(record)
      }
    };
  }

  /**
   * Розраховує загальне виробництво з тунелю
   */
  static calculateTotalProduction(tunelData) {
    if (!tunelData || typeof tunelData !== 'object') return 0;
    
    return Object.values(tunelData).reduce((sum, val) => {
      const num = this.parseNumber(val);
      return sum + (isNaN(num) ? 0 : num);
    }, 0);
  }

  /**
   * Парсить числа з різних форматів ("|||", "150", 150)
   */
  static parseNumber(value) {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      // Обробка форматів типу "|||" (3 палички = 3)
      if (value.includes('|')) {
        return value.split('|').length - 1;
      }
      // Обробка звичайних чисел
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }

  /**
   * Розраховує тривалість зміни в годинах
   */
  static calculateShiftDuration(record) {
    if (!record.createdAt || !record.closedAt) return 8; // За замовчуванням 8 год
    
    try {
      const start = new Date(record.createdAt);
      const end = new Date(record.closedAt);
      const durationHours = (end - start) / (1000 * 60 * 60);
      
      // Валідація реальної тривалості
      return durationHours > 0 && durationHours < 24 ? durationHours : 8;
    } catch (error) {
      return 8; // Значення за замовчуванням
    }
  }

  /**
   * Визначає сезон за місяцем
   */
  static getSeason(month) {
    // 0-зима, 1-весна, 2-літо, 3-осінь
    if (month >= 2 && month <= 4) return 1; // весна
    if (month >= 5 && month <= 7) return 2; // літо
    if (month >= 8 && month <= 10) return 3; // осінь
    return 0; // зима
  }

  /**
   * Нормалізує програми тунелю в числовий формат
   */
  static normalizePrograms(tunelData) {
    if (!tunelData) return {};
    
    const normalized = {};
    Object.entries(tunelData).forEach(([program, value]) => {
      normalized[program] = this.parseNumber(value);
    });
    
    return normalized;
  }

  /**
   * Розраховує загальну кількість циклів пральних машин
   */
  static calculateTotalCycles(lavCiclos) {
    if (!lavCiclos) return 0;
    
    return Object.values(lavCiclos).reduce((sum, cycles) => {
      return sum + this.parseNumber(cycles);
    }, 0);
  }

  /**
   * Розраховує відсоток браку
   */
  static calculateRejectionRate(record) {
    const totalProduction = this.calculateTotalProduction(record.tunel);
    const totalRejection = this.calculateTotalProduction(record.rech_tunel);
    
    if (totalProduction === 0) return 0;
    
    return (totalRejection / totalProduction) * 100;
  }

  /**
   * Розраховує ефективність (виробництво за годину)
   */
  static calculateEfficiency(record) {
    const totalProduction = this.calculateTotalProduction(record.tunel);
    const duration = this.calculateShiftDuration(record);
    
    if (duration === 0) return 0;
    
    return totalProduction / duration;
  }

  /**
   * Перевіряє чи запис містить повні дані
   */
  static isRecordComplete(record) {
    const hasProduction = this.calculateTotalProduction(record.tunel) > 0;
    const hasDate = !!record.fecha;
    const hasShift = !!record.turno;
    
    return hasProduction && hasDate && hasShift;
  }

  /**
   * Перевіряє наявність даних про ресурси
   */
  static hasResourceData(contData) {
    if (!contData) return false;
    
    return Object.values(contData).some(value => 
      value !== null && value !== undefined && value !== '' && value !== 0
    );
  }

  /**
   * Оцінює споживання електрики (на основі виробництва)
   */
  static estimateElectricity(record) {
    const production = this.calculateTotalProduction(record.tunel);
    const cycles = this.calculateTotalCycles(record.lav_ciclos);
    
    // Емпірична формула: 0.5 кВт/кг виробництва + 2 кВт/цикл
    return (production * 0.5) + (cycles * 2);
  }

  /**
   * Розраховує впевненість у якості даних
   */
  static calculateDataConfidence(record) {
    let confidence = 0;
    
    // +30% якщо є виробництво
    if (this.calculateTotalProduction(record.tunel) > 0) confidence += 30;
    
    // +20% якщо є дані про ресурси
    if (this.hasResourceData(record.cont)) confidence += 20;
    
    // +20% якщо є оператори
    if (record.operators && record.operators.length > 0) confidence += 20;
    
    // +30% якщо є тривалість зміни
    if (record.createdAt && record.closedAt) confidence += 30;
    
    return Math.min(confidence, 100);
  }

  /**
   * Парсить споживання ресурсів
   */
  static parseConsumption(value) {
    const num = this.parseNumber(value);
    return num > 0 ? num : 0;
  }

  /**
   * Підраховує кількість активних програм
   */
  static countActivePrograms(tunelData) {
    if (!tunelData) return 0;
    
    return Object.values(tunelData).filter(value => 
      this.parseNumber(value) > 0
    ).length;
  }
}

export default MLDataPreprocessor;


